/**
 * Admin Service
 *
 * Handles admin-only operations for managing user entitlements.
 * All functions include client-side admin check for safety,
 * but actual authorization is enforced by RLS and RPC guards.
 */

import { supabase } from '@/lib/supabase'
import type {
  UserAccessData,
  SetUserAccessParams,
  AdminUserRow,
} from '@/types/admin'

const ADMIN_UID = import.meta.env.VITE_ADMIN_UID

const ADMIN_EMAIL = 'davehail@me.com'

/**
 * Check if a user ID matches the configured admin UID.
 * Used for frontend UI guards only - not for security.
 */
export function isAdmin(userId: string | undefined): boolean {
  if (!userId || !ADMIN_UID) return false
  return userId === ADMIN_UID
}

/**
 * Check if a user email matches the admin email.
 * Used for frontend UI guards only - not for security.
 */
export function isAdminEmail(email: string | undefined): boolean {
  if (!email) return false
  return email === ADMIN_EMAIL
}

/**
 * Fetch all families with optional search filter.
 * Returns families ordered by creation date (newest first).
 */
export async function fetchFamilies(
  searchQuery?: string
): Promise<{ data: AdminUserRow[] | null; error: Error | null }> {
  try {
    let query = supabase
      .from('families')
      .select('id, auth_id, parent_email, parent_name, subscription_tier, subscription_status, created_at')
      .order('created_at', { ascending: false })

    if (searchQuery && searchQuery.trim()) {
      const term = `%${searchQuery.trim()}%`
      query = query.or(`parent_email.ilike.${term},parent_name.ilike.${term}`)
    }

    const { data, error } = await query.limit(100)

    if (error) {
      return { data: null, error: new Error(error.message) }
    }

    // Check for overrides to show indicator
    const authIds = data?.map((f) => f.auth_id) || []
    const { data: overrides } = await supabase
      .from('user_entitlement_overrides')
      .select('user_id')
      .in('user_id', authIds)

    const overrideSet = new Set(overrides?.map((o) => o.user_id) || [])

    const rows: AdminUserRow[] = (data || []).map((f) => ({
      id: f.id,
      authId: f.auth_id,
      email: f.parent_email,
      name: f.parent_name,
      tier: f.subscription_tier as 'free' | 'premium',
      status: f.subscription_status,
      createdAt: f.created_at,
      hasOverride: overrideSet.has(f.auth_id),
    }))

    return { data: rows, error: null }
  } catch (err) {
    return { data: null, error: err as Error }
  }
}

/**
 * Get detailed access data for a specific user via RPC.
 */
export async function getUserAccess(
  userId: string
): Promise<{ data: UserAccessData | null; error: Error | null }> {
  try {
    const { data, error } = await supabase.rpc('admin_get_user_access', {
      p_user_id: userId,
    })

    if (error) {
      return { data: null, error: new Error(error.message) }
    }

    return { data: data as UserAccessData, error: null }
  } catch (err) {
    return { data: null, error: err as Error }
  }
}

/**
 * Set user access via RPC (upserts override row).
 */
export async function setUserAccess(
  params: SetUserAccessParams
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.rpc('admin_set_user_access', {
      p_user_id: params.userId,
      p_premium_enabled: params.premiumEnabled,
      p_beta_enabled: params.betaEnabled,
      p_disabled: params.disabled ?? false,
      p_override_expires_at: params.overrideExpiresAt?.toISOString() ?? null,
      p_note: params.note ?? null,
    })

    if (error) {
      return { error: new Error(error.message) }
    }

    return { error: null }
  } catch (err) {
    return { error: err as Error }
  }
}

/**
 * Clear user override via RPC (deletes override row).
 */
export async function clearUserOverride(
  userId: string
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.rpc('admin_clear_user_override', {
      p_user_id: userId,
    })

    if (error) {
      return { error: new Error(error.message) }
    }

    return { error: null }
  } catch (err) {
    return { error: err as Error }
  }
}

/**
 * Fetch the current user's override (for entitlement computation).
 * This is called on login to determine effective access.
 */
export async function fetchCurrentUserOverride(
  userId: string
): Promise<{ data: import('@/types/admin').UserEntitlementOverride | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('user_entitlement_overrides')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      return { data: null, error: new Error(error.message) }
    }

    return { data, error: null }
  } catch (err) {
    return { data: null, error: err as Error }
  }
}
